import { Box, Card, Grid, MenuItem, Select, Skeleton, Typography } from "@mui/material";
import { DashboardCard, DashboardGrid } from "./styles";
import { useEffect, useMemo, useState } from "react";
import { useContas } from "../../../financeiro/hooks/useContas";
import { ReferenciaItem } from "../../../financeiro/enum/ReferenciaItem";
import { useMoeda } from "../../hooks/useMoeda";
import { useClientes } from "../../../clientes/hooks/useClientes";
import { IVendaMes } from "../../../financeiro/interfaces/IVendaMes";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useThemeContext } from "../../contexts/ThemeContext";
import { useTamanhoTela } from "../../hooks/useTamanhoTela";
import { useAutenticacaoContext } from "../../contexts/AutenticacaoContext";
import { MarketplacesDashboardCard } from "../../components/MarketplacesDashboardCard";
import { DashboardFiltroToggle } from "./components/DashboardFiltroToggle";
import { MobileDatePicker } from "@mui/x-date-pickers";
import moment from "moment";


export const Dashboard = () => {
    const { theme } = useThemeContext();
    const { modeloTela } = useTamanhoTela();
    const [ticketMedio, setTicketMedio] = useState<undefined | number>();
    const [valorTotalProdutos, setValorTotalProdutos] = useState<undefined | number>();
    const [valorTotalEntregas, setValorTotalEntregas] = useState<undefined | number>();
    const [totalClientes, setTotalClientes] = useState<undefined | number>();
    const [vendasMes, setVendasMes] = useState<undefined | IVendaMes[]>();
    const [ano, setAno] = useState<number>(new Date().getFullYear());
    const [modoFiltro, setModoFiltro] = useState<'ano' | 'data'>('ano');
    const [dataInicio, setDataInicio] = useState<string>(moment().format('YYYY-MM-DD'));
    const [dataFim, setDataFim] = useState<string>(moment().format('YYYY-MM-DD'));
    const [loadingProdutos, setLoadingProdutos] = useState(true);
    const [loadingEntregas, setLoadingEntregas] = useState(true);
    const [loadingTicketMedio, setLoadingTicketMedio] = useState(true);
    const [loadingClientes, setLoadingClientes] = useState(true);
    const [loadingVendasMes, setLoadingVendasMes] = useState(true);
    const { verificarAcessoFuncionalidade, perfil } = useAutenticacaoContext();

    const anos = useMemo(() =>
        Array.from({ length: new Date().getFullYear() - 2020 + 1 }, (_, i) => 2020 + i),
    []);

    const { consultarTotal } = useClientes();
    const { consultarTicketMedio, consultarValorTotal, consultarVendasMes } = useContas();

    const efetuarConsultasPorAno = (anoConsulta: number) => {
        setLoadingProdutos(true);
        setLoadingEntregas(true);
        setLoadingTicketMedio(true);
        setLoadingClientes(true);
        setLoadingVendasMes(true);

        const filtro = { ano: anoConsulta };

        consultarTicketMedio(filtro).then(r => {
            setTicketMedio(r ?? 0);
            setLoadingTicketMedio(false);
        });

        consultarValorTotal(ReferenciaItem.produto, filtro).then(r => {
            setValorTotalProdutos(r ?? 0);
            setLoadingProdutos(false);
        });

        consultarValorTotal(ReferenciaItem.entrega, filtro).then(r => {
            setValorTotalEntregas(r ?? 0);
            setLoadingEntregas(false);
        });

        consultarTotal().then(r => {
            setTotalClientes(r ?? 0);
            setLoadingClientes(false);
        });

        consultarVendasMes(filtro).then(r => {
            if (r) setVendasMes(r);
            setLoadingVendasMes(false);
        });
    }

    const efetuarConsultasPorData = (inicio: string, fim: string) => {
        setLoadingProdutos(true);
        setLoadingEntregas(true);
        setLoadingTicketMedio(true);
        setLoadingClientes(true);
        setLoadingVendasMes(true);

        const filtro = {
            dataInicio: `${inicio} 00:00:00`,
            dataFim: `${fim} 23:59:59`,
        };

        consultarTicketMedio(filtro).then(r => {
            setTicketMedio(r ?? 0);
            setLoadingTicketMedio(false);
        });

        consultarValorTotal(ReferenciaItem.produto, filtro).then(r => {
            setValorTotalProdutos(r ?? 0);
            setLoadingProdutos(false);
        });

        consultarValorTotal(ReferenciaItem.entrega, filtro).then(r => {
            setValorTotalEntregas(r ?? 0);
            setLoadingEntregas(false);
        });

        consultarTotal().then(r => {
            setTotalClientes(r ?? 0);
            setLoadingClientes(false);
        });

        consultarVendasMes(filtro).then(r => {
            if (r) setVendasMes(r);
            setLoadingVendasMes(false);
        });
    }

    const { real } = useMoeda();

    useEffect(() => {
        if (perfil && verificarAcessoFuncionalidade("Dashboard inicial")) {
            if (modoFiltro === 'ano') {
                efetuarConsultasPorAno(ano);
                const intervalId = setInterval(() => efetuarConsultasPorAno(ano), 30000);
                return () => clearInterval(intervalId);
            }
        }
    }, [perfil, ano, modoFiltro]);

    useEffect(() => {
        if (perfil && verificarAcessoFuncionalidade("Dashboard inicial")) {
            if (modoFiltro === 'data') {
                efetuarConsultasPorData(dataInicio, dataFim);
            }
        }
    }, [perfil, modoFiltro, dataInicio, dataFim]);

    const variantResultado = useMemo(() => {
        if (!valorTotalProdutos || valorTotalProdutos < 10000000) {
            return "h5";
        }
        return "h6"
    }, [valorTotalProdutos])

    const tituloGrafico = useMemo(() => {
        if (modoFiltro === 'data') {
            return dataInicio === dataFim
                ? `Vendas em ${moment(dataInicio).format('DD/MM/YYYY')}`
                : `Vendas de ${moment(dataInicio).format('DD/MM/YYYY')} até ${moment(dataFim).format('DD/MM/YYYY')}`;
        }
        return `Vendas em ${ano}`;
    }, [modoFiltro, ano, dataInicio, dataFim]);

    const CustomToolTip = (props: any) => {
        const { payload } = props;
        return (
            <Box>
                {payload?.map((item: any, i: number) => (
                    <Typography key={i} variant="subtitle2" fontWeight={"bold"}>
                        {real(item.value)}
                    </Typography>
                ))}
            </Box>
        );
    };

    if (perfil && !verificarAcessoFuncionalidade("Dashboard inicial")) {
        return (
            <Box sx={{ width: "100%", padding: { xs: 2, md: 3 } }}>
                <Grid sx={{ justifyContent: "center" }} spacing={3} container>
                    <DashboardGrid item xs={12} md={6} lg={4}>
                        <MarketplacesDashboardCard />
                    </DashboardGrid>
                </Grid>
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%", padding: { xs: 2, md: 3 } }}>
            <Grid sx={{ justifyContent: "center" }} spacing={3} container>
                <DashboardGrid item xs={12} sm={6} lg={3}>
                    <DashboardCard>
                        <Box>
                            <Typography variant="subtitle2">
                                PRODUTOS VENDIDOS
                            </Typography>
                            {loadingProdutos
                                ? <Skeleton variant="text" width={140} height={48} />
                                : <Typography variant={variantResultado} fontWeight={"bold"}>{real(valorTotalProdutos)}</Typography>
                            }
                        </Box>
                        <Box>
                            <img
                                src={`${process.env.PUBLIC_URL}/images/produtos.png`}
                                alt="Produtos"
                            />
                        </Box>
                    </DashboardCard>
                </DashboardGrid>
                <DashboardGrid item xs={12} sm={6} lg={3}>
                    <DashboardCard>
                        <Box>
                            <Typography variant="subtitle2">
                                ENTREGAS REALIZADAS
                            </Typography>
                            {loadingEntregas
                                ? <Skeleton variant="text" width={140} height={48} />
                                : <Typography variant={variantResultado} fontWeight={"bold"}>{real(valorTotalEntregas)}</Typography>
                            }
                        </Box>
                        <Box>
                            <img
                                src={`${process.env.PUBLIC_URL}/images/delivery.png`}
                                alt="Entregas"
                            />
                        </Box>
                    </DashboardCard>
                </DashboardGrid>
                <DashboardGrid item xs={12} sm={6} lg={3}>
                    <DashboardCard>
                        <Box>
                            <Typography variant="subtitle2">
                                TICKET MÉDIO
                            </Typography>
                            {loadingTicketMedio
                                ? <Skeleton variant="text" width={140} height={48} />
                                : <Typography variant={variantResultado} fontWeight={"bold"}>{real(ticketMedio)}</Typography>
                            }
                        </Box>
                        <Box>
                            <img
                                src={`${process.env.PUBLIC_URL}/images/ticket-medio.png`}
                                alt="Ticker médio"
                            />
                        </Box>
                    </DashboardCard>
                </DashboardGrid>
                <DashboardGrid item xs={12} sm={6} lg={3}>
                    <DashboardCard>
                        <Box>
                            <Typography variant="subtitle2">
                                CLIENTES
                            </Typography>
                            {loadingClientes
                                ? <Skeleton variant="text" width={80} height={48} />
                                : <Typography variant={variantResultado} fontWeight={"bold"}>{totalClientes}</Typography>
                            }
                        </Box>
                        <Box>
                            <img
                                src={`${process.env.PUBLIC_URL}/images/cliente.png`}
                                alt="Clientes"
                            />
                        </Box>
                    </DashboardCard>
                </DashboardGrid>
                <DashboardGrid item xs={12} lg={8}>
                    <Card sx={{
                        width: "100%",
                        height: "500px",
                        padding: "2rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                        borderRadius: '16px',
                        transition: 'box-shadow 0.3s ease-in-out, transform 0.2s ease-in-out',
                        '&:hover': {
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                            transform: 'translateY(-2px)',
                        }
                    }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem", flexWrap: "wrap" }}>
                            <Typography variant="h5" fontWeight={"bold"}>{tituloGrafico}</Typography>
                            <DashboardFiltroToggle value={modoFiltro} onChange={setModoFiltro} />
                            {modoFiltro === 'ano' && (
                                <Select
                                    size="small"
                                    value={ano}
                                    onChange={(e) => setAno(Number(e.target.value))}
                                >
                                    {anos.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                                </Select>
                            )}
                            {modoFiltro === 'data' && (
                                <Box sx={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                                    <MobileDatePicker
                                        label="Data início"
                                        value={moment(dataInicio)}
                                        onChange={(v) => v && setDataInicio(v.format('YYYY-MM-DD'))}
                                        slotProps={{ textField: { size: "small" } }}
                                    />
                                    <MobileDatePicker
                                        label="Data fim"
                                        value={moment(dataFim)}
                                        onChange={(v) => v && setDataFim(v.format('YYYY-MM-DD'))}
                                        minDate={moment(dataInicio)}
                                        slotProps={{ textField: { size: "small" } }}
                                    />
                                </Box>
                            )}
                        </Box>
                        {loadingVendasMes
                            ? <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: "8px" }} />
                            : <ResponsiveContainer width={"100%"} height={"100%"}>
                                <BarChart
                                    data={modeloTela == "desktop" ? vendasMes : vendasMes?.slice(-3)}
                                    margin={{ top: 5, right: 40, left: 60, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="mes" angle={0} height={30} interval={0} tick={{ fontSize: modoFiltro === 'data' ? 10 : 12 }} />
                                    <YAxis
                                        dataKey="valor"
                                        tickFormatter={(valor: string) => real(valor)}
                                    />
                                    <Tooltip content={CustomToolTip} />
                                    <Legend />
                                    <Bar
                                        dataKey="valor"
                                        fill={theme.palette.secondary.main}
                                        activeBar={{ fill: "pink", stroke: "blue" }}
                                        name="Valor de Vendas"
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        }
                    </Card>
                </DashboardGrid>
                <DashboardGrid item xs={12} lg={4}>
                    <MarketplacesDashboardCard />
                </DashboardGrid>
            </Grid>
        </Box>
    );
}
